import { CalendarDays } from 'lucide-react';
import { useRef, useState, type ComponentProps } from 'react';
import { Input } from '@/components/ui/input';
import { formatUsDate, maskUsDate, parsePastedDate, pasteDate } from '@/utils/parse-pasted-date';
import { calendarLabel, US_DATE_PLACEHOLDER } from './date-input-messages';

/** Older browsers (Safari before 16) cannot open the calendar from a script, so no dead button is shown. */
function canOpenCalendar(): boolean {
  return typeof HTMLInputElement !== 'undefined' && 'showPicker' in HTMLInputElement.prototype;
}

type UsDateInputProps = Omit<ComponentProps<'input'>, 'value' | 'onChange' | 'type'> & {
  /** The date as ISO `YYYY-MM-DD`, or an empty string while there is no complete valid date. */
  readonly value: string;
  onValueChange: (isoDate: string) => void;
  /**
   * When true, text that is not a complete valid date is handed to the form as typed instead of as an
   * empty string, so the form can tell "something invalid" from "nothing" and say so. Only for a form
   * whose validator rejects a non-date value; a filter that compares values as ISO strings keeps the
   * default.
   */
  readonly reportInvalidText?: boolean;
};

/**
 * A date field that always reads and writes `MM/DD/YYYY`.
 *
 * `<input type="date">` shows the browser's own locale — day first on a Latin American machine — and a
 * page cannot change that, so the field is a text input that owns the format. The value handed to the
 * form is still ISO, exactly what the native input gave, and an incomplete or impossible date is an
 * empty string, as before. Typing, pasting (US, ISO or MMDDYYYY) and the calendar button all land in
 * the same place; what is shown is tidied to `MM/DD/YYYY` when the field is left.
 */
export function UsDateInput({
  value,
  onValueChange,
  reportInvalidText = false,
  className,
  ...inputProps
}: UsDateInputProps): React.JSX.Element {
  const [text, setText] = useState(() => formatUsDate(value));
  const [lastValue, setLastValue] = useState(value);
  const pickerRef = useRef<HTMLInputElement>(null);

  // A change made from outside (a reset, a cleared filter) replaces what is shown. The user's own
  // typing never does: it updates `lastValue` first, so it is not mistaken for one.
  if (value !== lastValue) {
    setLastValue(value);
    setText(formatUsDate(value));
  }

  const commit = (isoDate: string, shown: string): void => {
    setText(shown);
    setLastValue(isoDate);
    onValueChange(isoDate);
  };

  return (
    <span className="relative block">
      <Input
        {...inputProps}
        type="text"
        autoComplete="off"
        placeholder={US_DATE_PLACEHOLDER}
        value={text}
        onChange={(event): void => {
          const typed = maskUsDate(event.target.value);
          commit(parsePastedDate(typed) ?? (reportInvalidText ? typed.trim() : ''), typed);
        }}
        onPaste={pasteDate((isoDate): void => {
          commit(isoDate, formatUsDate(isoDate));
        })}
        onBlur={(event): void => {
          const isoDate = parsePastedDate(text);
          if (isoDate !== null) {
            setText(formatUsDate(isoDate));
          }
          inputProps.onBlur?.(event);
        }}
        className={`${className ?? ''} pr-9`}
      />
      {canOpenCalendar() && (
        <button
          type="button"
          aria-label={calendarLabel(inputProps['aria-label'])}
          disabled={inputProps.disabled === true || inputProps.readOnly === true}
          onClick={(): void => {
            try {
              pickerRef.current?.showPicker();
            } catch {
              // A browser that refuses it leaves the typed field as the way in.
            }
          }}
          className="text-subtle hover:text-brand-600 focus-visible:text-brand-600 absolute inset-y-0 right-2 flex items-center disabled:cursor-not-allowed"
        >
          <CalendarDays aria-hidden="true" className="h-4 w-4" />
        </button>
      )}
      {/* The browser's calendar, kept out of sight and out of the tab order: it only supplies the
          picked day, which is shown in US order like everything else. */}
      <input
        ref={pickerRef}
        type="date"
        tabIndex={-1}
        aria-hidden="true"
        value={value}
        onChange={(event): void => {
          const picked = event.target.value;
          commit(picked, formatUsDate(picked));
        }}
        className="pointer-events-none absolute right-0 bottom-0 h-0 w-0 opacity-0"
      />
    </span>
  );
}
